import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98] transition-transform duration-75",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 raven-glow uppercase tracking-wide font-semibold",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-primary/50 bg-background hover:bg-primary/10 hover:border-primary text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent/50 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        raven: "bg-gradient-to-r from-raven-electric to-raven-violet text-white hover:from-raven-electric/90 hover:to-raven-violet/90 raven-glow-violet uppercase tracking-wide font-semibold shadow-lg",
        premium: "relative overflow-hidden bg-gradient-to-r from-raven-electric via-raven-violet to-raven-electric bg-200 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-bold shadow-2xl shadow-raven-electric/50 hover:shadow-raven-violet/50 hover:scale-105 transition-all duration-500",
        glass: "backdrop-blur-xl bg-white/10 border-2 border-white/20 hover:border-white/40 hover:bg-white/20 text-white font-semibold transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2 md:h-10 md:px-4 md:py-2 min-h-[44px] md:min-h-[40px]",
        sm: "h-9 rounded-md px-3 min-h-[36px]",
        lg: "h-11 rounded-md px-8 min-h-[48px] text-base",
        icon: "h-10 w-10 min-h-[44px] min-w-[44px] md:h-10 md:w-10 md:min-h-[40px] md:min-w-[40px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
